// Context provider for the generic first-run "coach mark" system.
//
// Loads the caller's dismissed-hint set once at mount (from
// GET /api/user-hints/dismissed) and exposes:
//   - isDismissed(hintKey)   — sync bool the <CoachMark> component uses
//                              to short-circuit rendering the popover.
//   - dismiss(hintKey)       — optimistic dismiss: adds to the in-memory
//                              set immediately, then POSTs. Rollback on
//                              network failure so the hint reappears
//                              instead of silently disappearing forever.
//
// Adding a new hint is a pure frontend change: pick a fresh dot-namespaced
// key ("page.element") and drop <CoachMark hintKey="…"> around the target.
// No context/API/reducer changes per hint.
//
// Load is skipped when the user isn't authenticated yet — the provider
// still renders, isDismissed() returns true (fail-closed so hints don't
// flash before login), and the fetch is retried the first time an
// authenticated child asks about a key.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { UserHintsService } from "api/userHints.api";

const UserHintsContext = createContext({
  loaded: false,
  isDismissed: () => true,
  dismiss: () => {},
});

export const UserHintsProvider = ({ children }) => {
  const [dismissed, setDismissed] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);
  const inFlightRef = useRef(false);

  const user = useSelector((state) => state.auth?.user);
  const isAuthed = Boolean(
    user?.userDetails?.organizationId ?? user?.organizationId
  );

  useEffect(() => {
    if (!isAuthed || loaded || inFlightRef.current) return;
    inFlightRef.current = true;
    (async () => {
      try {
        const res = await UserHintsService.listDismissed();
        const keys = res?.data?.data ?? [];
        setDismissed(new Set(keys));
        setLoaded(true);
      } catch {
        // Fail-closed on error — treat all hints as dismissed so we
        // don't flash a coach mark and then have it disappear the next
        // time the load succeeds. Retried on the next auth change.
        setLoaded(true);
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [isAuthed, loaded]);

  const isDismissed = useCallback(
    (key) => {
      // Before the initial load resolves, treat everything as dismissed
      // so hints don't flash and then vanish once the real set arrives.
      if (!loaded) return true;
      return dismissed.has(key);
    },
    [dismissed, loaded]
  );

  const dismiss = useCallback(
    async (key) => {
      if (!key || dismissed.has(key)) return;
      // Optimistic — add locally before the network round-trip so the
      // popover unmounts immediately.
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      try {
        await UserHintsService.dismiss(key);
      } catch {
        // Roll back so the user isn't stuck with a dismissal that
        // never made it to the server (would re-appear on refresh
        // anyway; matching that here keeps the two views coherent).
        setDismissed((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [dismissed]
  );

  const value = useMemo(
    () => ({ loaded, isDismissed, dismiss }),
    [loaded, isDismissed, dismiss]
  );

  return (
    <UserHintsContext.Provider value={value}>
      {children}
    </UserHintsContext.Provider>
  );
};

export const useUserHints = () => useContext(UserHintsContext);

export default UserHintsContext;
